// The Grants page: bulk grant and revoke on the access store through fga-api
// (ADR-0047). Self-scoping by design (ADR-0033): every object suggestion comes
// from `POST /api/list-objects` with relation `admin`, so the caller only sees
// targets they administer, and fga-api remains the enforcer — there is no
// capability gate on this route.
import {
  faSitemap,
  faUserPlus,
  faUserShield,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Button,
  Checkbox,
  Grid,
  Icon,
  Input,
  Message,
  Section,
  Table,
  Tooltip,
} from "@intility/bifrost-react";
import Select from "@intility/bifrost-react-select";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useCompanies } from "~/api/catalog";
import {
  type AdminObjectType,
  type BulkRoleIntent,
  type BulkRolesResponse,
  bulkRevokeRoles,
  useAdminObjects,
} from "~/api/grants";
import { auth } from "~/auth";
import { PageLayout } from "~/components/layout/PageLayout";
import { BulkOutcomeModal } from "./BulkOutcomeModal";
import { GrantBuilder } from "./GrantBuilder";
import { type GrantObjectType, OBJECT_TYPE_LABEL, objectLabel } from "./labels";
import {
  ObjectAccessTable,
  type RevokeSelectionItem,
} from "./ObjectAccessTable";

const OBJECT_TYPES: AdminObjectType[] = ["organization", "company", "platform"];

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All types" },
  ...OBJECT_TYPES.map((type) => ({
    value: type,
    label: `${OBJECT_TYPE_LABEL[type]}s`,
  })),
];

interface ObjectRow {
  id: string;
  type: GrantObjectType;
  label: string;
}

export default function Grants() {
  const queryClient = useQueryClient();
  const sub = auth.keycloak.tokenParsed?.sub;
  const callerSubject = sub ? `user:${sub}` : null;

  const organizationQuery = useAdminObjects("organization", callerSubject);
  const companyQuery = useAdminObjects("company", callerSubject);
  const platformQuery = useAdminObjects("platform", callerSubject);
  const { data: companies } = useCompanies();

  const [selectedObjects, setSelectedObjects] = useState<Set<string>>(
    new Set(),
  );
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [revokeSelection, setRevokeSelection] = useState<
    Map<string, RevokeSelectionItem>
  >(new Map());
  const [revokePreview, setRevokePreview] = useState<BulkRolesResponse | null>(
    null,
  );
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revokeResult, setRevokeResult] = useState<BulkRolesResponse | null>(
    null,
  );

  const adminLoading =
    organizationQuery.isLoading ||
    companyQuery.isLoading ||
    platformQuery.isLoading;
  const adminError =
    organizationQuery.error ?? companyQuery.error ?? platformQuery.error;

  const objectRows = useMemo<ObjectRow[]>(() => {
    const rows: ObjectRow[] = [];
    const byType: [AdminObjectType, string[] | undefined][] = [
      ["organization", organizationQuery.data],
      ["company", companyQuery.data],
      ["platform", platformQuery.data],
    ];
    for (const [type, ids] of byType) {
      for (const id of ids ?? []) {
        rows.push({ id, type, label: objectLabel(id, companies) });
      }
    }
    rows.sort((a, b) => a.label.localeCompare(b.label));
    return rows;
  }, [
    organizationQuery.data,
    companyQuery.data,
    platformQuery.data,
    companies,
  ]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return objectRows.filter((row) => {
      if (typeFilter !== "all" && row.type !== typeFilter) return false;
      if (!query) return true;
      return (
        row.label.toLowerCase().includes(query) ||
        row.id.toLowerCase().includes(query)
      );
    });
  }, [objectRows, search, typeFilter]);

  const selectedObjectIds = useMemo(
    () => [...selectedObjects],
    [selectedObjects],
  );

  // Selected objects and staged revoke rows must move together: a row staged
  // under an object that is no longer selected must not ride along in a later
  // revoke — the user could no longer see what they were about to remove.
  function applyObjectSelection(next: Set<string>) {
    setSelectedObjects(next);
    setRevokeSelection((prev) => {
      const nextSelection = new Map(prev);
      for (const key of prev.keys()) {
        const objectId = key.slice(0, key.indexOf("|"));
        if (!next.has(objectId)) nextSelection.delete(key);
      }
      return nextSelection;
    });
  }

  function toggleObject(id: string) {
    const next = new Set(selectedObjects);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    applyObjectSelection(next);
  }

  // Only the rows on screen: with a search or filter active, hidden rows stay
  // untouched (same rule as SeedPanel).
  function selectVisible() {
    applyObjectSelection(new Set(visibleRows.map((row) => row.id)));
  }

  function deselectAll() {
    applyObjectSelection(new Set());
  }

  function toggleRevokeRow(key: string, item: RevokeSelectionItem | null) {
    setRevokeSelection((prev) => {
      const next = new Map(prev);
      if (item) next.set(key, item);
      else next.delete(key);
      return next;
    });
  }

  function invalidateAccess() {
    setRevokeSelection(new Map());
    return queryClient.invalidateQueries({ queryKey: ["grants", "access"] });
  }

  const revokeItems: BulkRoleIntent[] = useMemo(
    () =>
      [...revokeSelection.values()].map((item) => ({
        user: item.subject,
        relation: item.relation,
        object: item.objectId,
      })),
    [revokeSelection],
  );

  async function handleRevokePreview() {
    setRevokeBusy(true);
    setRevokeError(null);
    setRevokeResult(null);
    try {
      setRevokePreview(await bulkRevokeRoles(revokeItems, true));
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : "Preview failed.");
    } finally {
      setRevokeBusy(false);
    }
  }

  async function handleRevokeConfirm() {
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      const result = await bulkRevokeRoles(revokeItems, false);
      setRevokePreview(null);
      setRevokeResult(result);
      await invalidateAccess();
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : "Revoke failed.");
    } finally {
      setRevokeBusy(false);
    }
  }

  return (
    <PageLayout
      title="Grants"
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Grants" }]}
      description="Grant and revoke access in bulk on the organizations, companies, and platforms you hold admin on. A role on an object cascades to everything beneath it."
    >
      <Section>
        <Section.Content>
          <Grid gap={16}>
            <Grid gap={4}>
              <span className="bf-h4">
                <Icon
                  icon={faSitemap}
                  style={{ marginRight: 8, color: "var(--bfc-theme)" }}
                />
                Target objects
              </span>
              <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
                Only the objects you hold admin on are listed. Select one or
                many; the same platform under different companies appears as one
                row per company.
              </p>
            </Grid>

            {adminLoading ? (
              <Grid gap={16} style={{ minHeight: 80, placeContent: "center" }}>
                <Icon.Spinner size={24} />
              </Grid>
            ) : adminError ? (
              <Message state="alert" header="Could not load your objects">
                {adminError instanceof Error
                  ? adminError.message
                  : "Unknown error"}
              </Message>
            ) : objectRows.length === 0 ? (
              <Message state="neutral">
                You do not hold admin on any organization, company, or platform,
                so there is nothing to grant through this page.
              </Message>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                  }}
                >
                  <Input
                    label="Search"
                    placeholder="Name or object id…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 320 }}
                  />
                  <Select
                    label="Type"
                    options={TYPE_FILTER_OPTIONS}
                    value={TYPE_FILTER_OPTIONS.find(
                      (o) => o.value === typeFilter,
                    )}
                    onChange={(opt) =>
                      opt && setTypeFilter(opt.value as string)
                    }
                    style={{ maxWidth: 200 }}
                  />
                  <Button small variant="flat" onClick={selectVisible}>
                    Select all
                  </Button>
                  <Button small variant="flat" onClick={deselectAll}>
                    Deselect all
                  </Button>
                </div>

                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell
                        aria-label="Select"
                        style={{ width: 40 }}
                      />
                      <Table.HeaderCell>Type</Table.HeaderCell>
                      <Table.HeaderCell>Name</Table.HeaderCell>
                      <Table.HeaderCell>Object</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {visibleRows.length === 0 ? (
                      <Table.Row>
                        <Table.Cell />
                        <Table.Cell />
                        <Table.Cell>No objects match your search.</Table.Cell>
                        <Table.Cell />
                      </Table.Row>
                    ) : (
                      visibleRows.map((row) => (
                        <Table.Row key={row.id}>
                          <Table.Cell>
                            <Checkbox
                              label={`Select ${row.label}`}
                              hideLabel
                              checked={selectedObjects.has(row.id)}
                              onChange={() => toggleObject(row.id)}
                            />
                          </Table.Cell>
                          <Table.Cell>{OBJECT_TYPE_LABEL[row.type]}</Table.Cell>
                          <Table.Cell>{row.label}</Table.Cell>
                          <Table.Cell>
                            <Tooltip content={row.id}>
                              <code>{row.id}</code>
                            </Tooltip>
                          </Table.Cell>
                        </Table.Row>
                      ))
                    )}
                  </Table.Body>
                </Table>
              </>
            )}
          </Grid>
        </Section.Content>
      </Section>

      {selectedObjectIds.length > 0 && (
        <Section>
          <Section.Content>
            <Grid gap={16}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <Grid gap={4}>
                  <span className="bf-h4">
                    <Icon
                      icon={faUserShield}
                      style={{ marginRight: 8, color: "var(--bfc-theme)" }}
                    />
                    Current access
                  </span>
                  <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
                    Everyone who holds access through the selected objects.
                    Direct grants can be revoked here; inherited ones are
                    read-only with their source.
                  </p>
                </Grid>
                <Button
                  variant="filled"
                  state={
                    revokeSelection.size === 0 || revokeBusy
                      ? "inactive"
                      : "alert"
                  }
                  onClick={
                    revokeSelection.size === 0 || revokeBusy
                      ? undefined
                      : handleRevokePreview
                  }
                >
                  {revokeSelection.size === 0
                    ? "Select rows to revoke"
                    : `Revoke selected (${revokeSelection.size})`}
                </Button>
              </div>

              {revokeError && (
                <Message
                  state="alert"
                  header="Revoke failed"
                  onClose={() => setRevokeError(null)}
                >
                  {revokeError}
                </Message>
              )}

              {revokeResult && (
                <Message
                  state={
                    revokeResult.counts.rejected > 0 ? "warning" : "success"
                  }
                  header="Revoke applied"
                  onClose={() => setRevokeResult(null)}
                >
                  {revokeResult.counts.revoked} revoked
                  {revokeResult.counts.unchanged > 0 &&
                    `, ${revokeResult.counts.unchanged} unchanged`}
                  {revokeResult.counts.rejected > 0 &&
                    `, ${revokeResult.counts.rejected} rejected`}
                  .
                  {revokeResult.items
                    .filter((i) => i.outcome === "rejected")
                    .map((i) => (
                      <span key={i.index} style={{ display: "block" }}>
                        {i.user} on {i.object}:{" "}
                        {i.message ?? i.code ?? "rejected"}
                      </span>
                    ))}
                </Message>
              )}

              {selectedObjectIds.map((objectId) => (
                <ObjectAccessTable
                  key={objectId}
                  objectId={objectId}
                  companies={companies}
                  selected={
                    new Set(
                      [...revokeSelection.keys()].filter((key) =>
                        key.startsWith(`${objectId}|`),
                      ),
                    )
                  }
                  onToggle={toggleRevokeRow}
                />
              ))}
            </Grid>
          </Section.Content>
        </Section>
      )}

      <Section>
        <Section.Content>
          <Grid gap={16}>
            <Grid gap={4}>
              <span className="bf-h4">
                <Icon
                  icon={faUserPlus}
                  style={{ marginRight: 8, color: "var(--bfc-theme)" }}
                />
                Grant access
              </span>
              <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
                Grant one role to users, groups, or clients on every selected
                target object, in one bulk operation with a preview first.
              </p>
            </Grid>
            <GrantBuilder
              targetObjects={selectedObjectIds}
              companies={companies}
              onApplied={() => {
                void invalidateAccess();
              }}
            />
          </Grid>
        </Section.Content>
      </Section>

      {revokePreview && (
        <BulkOutcomeModal
          mode="revoke"
          result={revokePreview}
          busy={revokeBusy}
          companies={companies}
          onConfirm={handleRevokeConfirm}
          onClose={() => setRevokePreview(null)}
        />
      )}
    </PageLayout>
  );
}
